import React, { Fragment } from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'
import { Row, Col } from 'react-flexbox-grid'
import get from 'lodash/get'

import { Form, Text, Checkbox, Select, DateTimePicker } from 'components/forms'
import { Modal } from 'components/Modal'
import { useQuery } from 'components/graphql'
import { getKitchenTimeSlots } from 'gql/query'
import Button from 'components/Button'
import Divider from 'components/Divider'
import styles from './ShopTimeSlot.module.scss'

const PopperContainer = ({ children }) => {
	const container = document.body
	return createPortal(
		children,
		container
	)
}

const getWeekDaysOptions = ({ t, existingDays }) => {
	const outArr = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday']
		.map(day => (
			<Checkbox
				key={day}
				field={`weekDays.${day}`}
				label={t(`common.dates.${day.toLowerCase()}`)}
				disabled={existingDays.indexOf(day) >= 0}
			/>
		))
	return(
		<Row>
			<Col xs={4}>{outArr.slice(0, 3)}</Col>
			<Col xs={4}>{outArr.slice(3, 6)}</Col>
			<Col xs={4}>{outArr.slice(6)}</Col>
		</Row>
	)
}

const TimeSlotForm = ({ formData: { existingDays = [], ...formData }, onSubmit, onClose, existingSlots = [] }) => {
	const isNewSlot = !formData.timeSlot
	const { data, queryField } = useQuery(getKitchenTimeSlots, { skip: !isNewSlot })
	const slotsOptions = get(data, [queryField, 'cashRegisterGlobal', 'kitchenTimeSlots'], [])
		.map(({ timeSlotCode, name }) => ({ value: timeSlotCode, label: name }))
	const [t] = useTranslation()
	const timeSlotValidation = val => {
		if(!val) {
			return t('validators.required')
		}
		const isExist = existingSlots.find(({ timeSlot }) => timeSlot === val)
		return isExist ? t('validators.duplicate') : null
	}
	const handleSubmit = ({ weekDays, ...vals }) => {
		const submitVals = { ...formData, ...vals }
		const out = weekDays
			// For each weekDay should be a separate 'openingHours' item
			? Object.keys(weekDays).map(day => ({ ...submitVals, day }))
			: [submitVals]
		onSubmit(out)
		onClose()
	}

	return(
		<Modal
			unmount={onClose}
			label={t('common.strings.edit_values')}
			width={440}
		>
			<Form
				initialValues={formData}
				onSubmit={handleSubmit}
				reinitByModVer={false}
			>
				{({ formState: { values, invalid }, formApi }) => {
					const isNewDay = !formData.day
					const canSubmit = () => {
						if(!isNewSlot && !isNewDay) {
							return true
						}
						const { weekDays = {}, day } = values
						return Object.keys(weekDays).some(weekDay => weekDays[weekDay]) || day
					}

					return(
						<Fragment>
							{isNewSlot &&
								<Fragment>
									<Select
										field='timeSlot'
										options={slotsOptions}
										label={t('common.fields.name')}
										onChange={({ label }) => formApi.setValue('description', label)}
										validate={timeSlotValidation}
										validateOnChange
										maxMenuHeight={210}
									/>
									<Text field='description' hidden />
									<Divider />
								</Fragment>
							}
							{isNewDay &&
								<Fragment>
									<p className='form-group-label'>{t('common.fields.week_days')}</p>
									{getWeekDaysOptions({ t, existingDays })}
									<Divider />
								</Fragment>
							}
							<DateTimePicker
								field='openingTime'
								picker='time'
								interval
								label={{ from: t('common.fields.from_time'), to: t('common.fields.to_time') }}
								addDateToTime={false}
								validate={val => !val || !val.from || !val.to ? t('validators.required') : undefined} // eslint-disable-line no-undefined
								validateOnChange
								popperContainer={PopperContainer}
								popperClassName={styles.popper}
							/>
							<DateTimePicker
								field='lunchBreak'
								picker='time'
								interval
								label={{ from: t('common.fields.lunch_break_start'), to: t('common.fields.lunch_break_end') }}
								addDateToTime={false}
								validate={val => !val || !val.from || !val.to ? t('validators.required') : undefined} // eslint-disable-line no-undefined
								validateOnChange
								popperContainer={PopperContainer}
								popperClassName={styles.popper}
							/>
							<div className={styles['buttons-wrapper']}>
								<Button
									text={t('common.buttons.abort')}
									status='cancelation'
									onClick={() => onClose()}
								/>
								<Button
									text={t('common.buttons.create')}
									type='submit'
									status={canSubmit() ? 'primary' : 'inactive'}
								/>
							</div>
						</Fragment>
					)
				}}
			</Form>
		</Modal>
	)
}

export default TimeSlotForm
